import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthStore } from '../store/auth.store';
import { GuestFlowStore } from '../services/guest-flow.store';
import { ROUTES } from '../routes';
import { waitForAuthHydration } from './wait-for-auth-hydration';

/**
 * Angular functional-guard equivalent of Consumer's core/components/GuestRoute.tsx
 * — blocks already-authenticated users from guest-only screens (login,
 * registration, forgot-password, not-eligible). Also enforces the
 * splash-back-lock (see GuestFlowStore doc comment): once Splash has
 * resolved eligibility for this session, browser-back must not re-enter
 * these guest screens — redirect to Splash instead of activating.
 *
 * Now *waits* for bootstrap hydration to finish (see
 * `wait-for-auth-hydration.ts`) before deciding — this guard previously
 * decided immediately against the default (pre-hydration) auth state, which
 * both risked briefly activating Login for an already-signed-in user, and
 * meant Login's avatar mounted before `LocalStorageManagerService` had
 * decrypted the cached profile picture/name.
 */
export const guestGuard: CanActivateFn = async () => {
  const authStore = inject(AuthStore);
  const guestFlowStore = inject(GuestFlowStore);
  const router = inject(Router);

  await waitForAuthHydration(authStore);

  if (authStore.isAuthenticated()) {
    return router.createUrlTree([`/${ROUTES.DASHBOARD}`]);
  }

  if (guestFlowStore.isSplashBackLocked()) {
    return router.createUrlTree([`/${ROUTES.LOGIN}`]);
  }

  return true;
};
